import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { FormField } from "./FormField";
import { TermsAcceptance } from "./TermsAcceptance";
import { EmailConfirmationScreen } from "./EmailConfirmationScreen";
import { validateRegistrationForm, validatePasswordRealTime, ValidationErrors, PasswordValidationResult } from "@/utils/registrationValidation";
import { useAuth, UserSignUpData } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useReferral } from "@/hooks/useReferral";

interface RegistrationFormProps {
  referralCode: string;
  processReferralToken?: (userEmail: string) => Promise<string | null>;
}

const CONFIRMATION_STORAGE_KEY = 'uplivion_registration_confirmation';

export const RegistrationForm = ({ referralCode, processReferralToken }: RegistrationFormProps) => {
  const { t } = useTranslation(['auth', 'common', 'validation']);
  const [userData, setUserData] = useState({
    username: "",
    firstName: "",
    lastName: "",
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidationResult>({
    isValid: false,
    requirements: { 
      minLength: false, 
      hasUppercase: false, 
      hasLowercase: false, 
      hasNumber: false, 
      hasSpecialChar: false 
    },
    strength: 'weak'
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [marketingAccepted, setMarketingAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { signUp } = useAuth();
  const { processStoredReferral } = useReferral();
  const navigate = useNavigate();

  // Check for stored confirmation state on component mount
  useEffect(() => {
    const storedConfirmationData = sessionStorage.getItem(CONFIRMATION_STORAGE_KEY);
    if (storedConfirmationData) {
      try {
        const { email: storedEmail } = JSON.parse(storedConfirmationData);
        setEmail(storedEmail);
        setShowConfirmation(true);
        console.log('Restored confirmation screen for email:', storedEmail);
      } catch (error) {
        console.error('Error parsing stored confirmation data:', error);
        sessionStorage.removeItem(CONFIRMATION_STORAGE_KEY);
      }
    }
  }, []);

  // Clear confirmation state when component unmounts or user navigates away
  useEffect(() => {
    return () => {
      // Only clear if we're not showing confirmation screen
      if (!showConfirmation) {
        sessionStorage.removeItem(CONFIRMATION_STORAGE_KEY);
      }
    };
  }, [showConfirmation]);

  // Real-time password validation
  useEffect(() => {
    const validation = validatePasswordRealTime(password);
    setPasswordValidation(validation);
  }, [password]);

  // Check if form is valid for button enabling
  const isFormValid = () => {
    return (
      email.trim() !== "" &&
      userData.username.trim() !== "" &&
      userData.firstName.trim() !== "" &&
      userData.lastName.trim() !== "" &&
      passwordValidation.isValid &&
      termsAccepted &&
      privacyAccepted
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    
    // Clear validation error when user starts typing
    if (validationErrors[id]) {
      setValidationErrors(prev => ({ ...prev, [id]: '' }));
    }
    
    if (id === "username" || id === "firstName" || id === "lastName") {
      setUserData(prev => ({ ...prev, [id]: value }));
    } else if (id === "email") {
      setEmail(value);
    } else if (id === "password") {
      setPassword(value);
    }
  };


  const handleTermsChange = (accepted: boolean) => {
    setTermsAccepted(accepted);
    if (validationErrors.terms) {
      setValidationErrors(prev => ({ ...prev, terms: '' }));
    }
  };

  const handlePrivacyChange = (accepted: boolean) => {
    setPrivacyAccepted(accepted);
    if (validationErrors.privacy) {
      setValidationErrors(prev => ({ ...prev, privacy: '' }));
    }
  };

  const validateTermsAcceptance = () => {
    const errors: ValidationErrors = {};
    
    if (!termsAccepted) {
      errors.terms = "Sie müssen den AGB zustimmen";
    }
    
    if (!privacyAccepted) {
      errors.privacy = "Sie müssen die Datenschutzerklärung akzeptieren";
    }
    
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('Form submitted');
    
    const formErrors = validateRegistrationForm(
      email,
      userData.username,
      userData.firstName,
      userData.lastName,
      password
    );

    const termsErrors = validateTermsAcceptance();
    const allErrors = { ...formErrors, ...termsErrors };

    if (Object.keys(allErrors).length > 0) {
      setValidationErrors(allErrors);
      console.log('Form validation failed:', allErrors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log('Attempting to sign up user with data:', {
        email,
        userData: { ...userData, referralCode, marketingConsent: marketingAccepted }
      });
      
      await signUp(email, password, { 
        ...userData, 
        birthdate: '', // Will be set later through timeline setup
        referralCode,
        marketingConsent: marketingAccepted 
      });
      console.log('Signup completed successfully, storing confirmation state');
      
      // Store confirmation state in sessionStorage to persist across re-renders
      sessionStorage.setItem(CONFIRMATION_STORAGE_KEY, JSON.stringify({ email }));
      
      // Show the confirmation screen
      setShowConfirmation(true);
      
      console.log('Confirmation screen should now be visible, state stored in sessionStorage');
      
    } catch (error) {
      console.error("Registration error:", error);
      // Clear any stored confirmation state on error
      sessionStorage.removeItem(CONFIRMATION_STORAGE_KEY);
      setShowConfirmation(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show confirmation screen after successful registration
  if (showConfirmation) {
    console.log('Rendering EmailConfirmationScreen for email:', email);
    return <EmailConfirmationScreen email={email} />;
  }

  return (
    <div className="w-full max-w-md bg-white rounded-lg shadow-md p-8">
      <h1 className="text-2xl font-bold text-center mb-6">{t('register.title')}</h1>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField
          id="email"
          label={t('auth:fields.email')}
          type="email"
          placeholder={t('auth:placeholders.email')}
          value={email}
          onChange={handleChange}
          error={validationErrors.email}
          required
        />

        <FormField
          id="username"
          label={t('auth:fields.username')}
          placeholder={t('auth:placeholders.username')}
          value={userData.username}
          onChange={handleChange}
          error={validationErrors.username}
          required
        />
        
        <div className="grid grid-cols-2 gap-4">
          <FormField
            id="firstName"
            label={t('auth:fields.firstName')}
            placeholder={t('auth:placeholders.firstName')}
            value={userData.firstName}
            onChange={handleChange}
            error={validationErrors.firstName}
            required
          />
          
          <FormField
            id="lastName"
            label={t('auth:fields.lastName')}
            placeholder={t('auth:placeholders.lastName')}
            value={userData.lastName}
            onChange={handleChange}
            error={validationErrors.lastName}
            required
          />
        </div>
        
        <div className="space-y-2">
          <FormField
            id="password"
            label={t('auth:fields.password')}
            type="password"
            placeholder={t('auth:fields.passwordPlaceholder')}
            value={password}
            onChange={handleChange}
            error={validationErrors.password}
            isInvalid={password.length > 0 && !passwordValidation.isValid}
            required
          />
          
          {/* Password Requirements */}
          {password.length > 0 && (
            <div className="mt-2 p-3 bg-gray-50 rounded-lg border">
              <p className="text-sm font-medium mb-2">
                {t('validation:password.requirements')}:
              </p>
              <div className="space-y-1 text-xs">
                <div className={`flex items-center gap-2 ${passwordValidation.requirements.minLength ? 'text-green-600' : 'text-red-500'}`}>
                  <span>{passwordValidation.requirements.minLength ? '✓' : '✗'}</span>
                  <span>{t('validation:password.minLength', { min: 8 })}</span>
                </div>
                <div className={`flex items-center gap-2 ${passwordValidation.requirements.hasUppercase ? 'text-green-600' : 'text-red-500'}`}>
                  <span>{passwordValidation.requirements.hasUppercase ? '✓' : '✗'}</span>
                  <span>{t('validation:password.uppercase')}</span>
                </div>
                <div className={`flex items-center gap-2 ${passwordValidation.requirements.hasLowercase ? 'text-green-600' : 'text-red-500'}`}>
                  <span>{passwordValidation.requirements.hasLowercase ? '✓' : '✗'}</span>
                  <span>{t('validation:password.lowercase')}</span>
                </div>
                <div className={`flex items-center gap-2 ${passwordValidation.requirements.hasNumber ? 'text-green-600' : 'text-red-500'}`}>
                  <span>{passwordValidation.requirements.hasNumber ? '✓' : '✗'}</span>
                  <span>{t('validation:password.number')}</span>
                </div>
                <div className={`flex items-center gap-2 ${passwordValidation.requirements.hasSpecialChar ? 'text-green-600' : 'text-red-500'}`}>
                  <span>{passwordValidation.requirements.hasSpecialChar ? '✓' : '✗'}</span>
                  <span>{t('validation:password.specialChar')}</span>
                </div>
              </div>
              
              {/* Password Strength Indicator */}
              <div className="mt-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium">Strength:</span>
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-300 ${
                        passwordValidation.strength === 'strong' ? 'bg-green-500 w-full' :
                        passwordValidation.strength === 'good' ? 'bg-yellow-500 w-3/4' :
                        passwordValidation.strength === 'fair' ? 'bg-orange-500 w-1/2' :
                        'bg-red-500 w-1/4'
                      }`}
                    />
                  </div>
                  <span className={`text-xs font-medium capitalize ${
                    passwordValidation.strength === 'strong' ? 'text-green-600' :
                    passwordValidation.strength === 'good' ? 'text-yellow-600' :
                    passwordValidation.strength === 'fair' ? 'text-orange-600' :
                    'text-red-600'
                  }`}>
                    {passwordValidation.strength}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <TermsAcceptance
          termsAccepted={termsAccepted}
          privacyAccepted={privacyAccepted}
          marketingAccepted={marketingAccepted}
          onTermsChange={handleTermsChange}
          onPrivacyChange={handlePrivacyChange}
          onMarketingChange={setMarketingAccepted}
          termsError={validationErrors.terms}
          privacyError={validationErrors.privacy}
        />
        
        <Button 
          type="submit" 
          className="w-full bg-uplivion-blue hover:bg-blue-700"
          disabled={isSubmitting || !isFormValid()}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('auth:register.loading')}
            </>
          ) : (
            t('common:buttons.register')
          )}
        </Button>
      </form>
    </div>
  );
};
